import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { RefreshCw } from 'lucide-react';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardTableStatusBadge,
  DashboardTableStatusTone,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@shigo-ui/ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import type { OwnerFormValue } from '../../formSchema';
import { OwnerStatus, SellersJsonStatus } from '../../model';
import { useOwnerMutations } from '../../hooks';

export function OwnerIdentityFields() {
  const { register, control, setValue, formState } = useFormContext<OwnerFormValue>();
  const ownerId = useWatch({ control, name: 'id' });
  const status = useWatch({ control, name: 'status' });
  const sellersStatus = useWatch({ control, name: 'sellersJsonStatus' });
  const lastRefreshed = useWatch({ control, name: 'sellersJsonLastRefreshed' });
  const failReason = useWatch({ control, name: 'sellersJsonFailReason' });
  const mutations = useOwnerMutations();

  async function refreshSellersJson() {
    if (!ownerId) return;
    try {
      const result = await mutations.refreshSellersJson.mutateAsync(ownerId);
      setValue('sellersJsonStatus', result.status, { shouldDirty: false });
      setValue('sellersJsonLastRefreshed', result.lastRefreshed ?? '', { shouldDirty: false });
      setValue('sellersJsonFailReason', result.failReason ?? '', { shouldDirty: false });
      toast.success(dashboardT('owners.sellersJson.refreshed'));
    } catch (error) {
      showRequestError(error, dashboardT('owners.sellersJson.refreshError'));
    }
  }

  return (
    <DashboardPageFormBody
      width="lg"
      title={dashboardT('owners.tabs.identity')}
      description={dashboardT('owners.identity.description')}
    >
      <div className="grid gap-5 md:grid-cols-2">
        <DashboardPageFormField
          label={dashboardT('owners.fields.name')}
          required
          htmlFor="owner-name"
          error={formState.errors.name?.message}
        >
          <Controller
            control={control}
            name="name"
            render={({ field }) => <Input id="owner-name" {...field} />}
          />
        </DashboardPageFormField>
        <DashboardPageFormField
          label={dashboardT('owners.fields.status')}
          required
          htmlFor="owner-status"
        >
          <Select
            value={status}
            onValueChange={(value) => {
              setValue('status', value as OwnerStatus, { shouldDirty: true });
            }}
          >
            <SelectTrigger id="owner-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(OwnerStatus).map((value) => (
                <SelectItem key={value} value={value}>
                  {ownerStatusText(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DashboardPageFormField>
        <DashboardPageFormField
          label={dashboardT('owners.fields.businessDomain')}
          hint={dashboardT('owners.hints.businessDomain')}
          htmlFor="owner-business-domain"
        >
          <Input id="owner-business-domain" {...register('businessDomain')} />
        </DashboardPageFormField>
        <div className="md:col-span-2">
          <DashboardPageFormField
            label={dashboardT('owners.fields.sellersJsonUrl')}
            hint={dashboardT('owners.hints.sellersJson')}
            htmlFor="owner-sellers-json-url"
            error={formState.errors.sellersJsonUrl?.message}
          >
            <Input id="owner-sellers-json-url" {...register('sellersJsonUrl')} />
          </DashboardPageFormField>
          <div className="border-input mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <DashboardTableStatusBadge tone={sellersJsonTone(sellersStatus)}>
                {sellersJsonStatusText(sellersStatus)}
              </DashboardTableStatusBadge>
              {lastRefreshed ? (
                <span className="text-muted-foreground text-xs">
                  {dashboardT('owners.sellersJson.lastRefreshed', {
                    value: formatDisplayDate(lastRefreshed, { keepInvalid: true }),
                  })}
                </span>
              ) : null}
              {failReason ? (
                <span className="text-destructive min-w-0 truncate text-xs" title={failReason}>
                  {failReason}
                </span>
              ) : null}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!ownerId || mutations.refreshSellersJson.isPending}
              onClick={() => void refreshSellersJson()}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {dashboardT('owners.sellersJson.refresh')}
            </Button>
          </div>
        </div>
      </div>
    </DashboardPageFormBody>
  );
}

function sellersJsonTone(status: SellersJsonStatus) {
  if (status === SellersJsonStatus.Valid) return DashboardTableStatusTone.Success;
  if (status === SellersJsonStatus.RefetchRequired) return DashboardTableStatusTone.Warning;
  return DashboardTableStatusTone.Neutral;
}

function ownerStatusText(status: OwnerStatus) {
  if (status === OwnerStatus.Active) return dashboardT('owners.status.active');
  if (status === OwnerStatus.Inactive) return dashboardT('owners.status.inactive');
  return dashboardT('owners.status.archived');
}

function sellersJsonStatusText(status: SellersJsonStatus) {
  if (status === SellersJsonStatus.Valid) return dashboardT('owners.sellersJson.valid');
  if (status === SellersJsonStatus.RefetchRequired) return dashboardT('owners.sellersJson.refetch');
  if (status === SellersJsonStatus.Invalid) return dashboardT('owners.sellersJson.invalid');
  return dashboardT('owners.sellersJson.unknown');
}
